import { useContext } from 'react';

import {
	DeleteOutlined,
	EditOutlined,
	EllipsisOutlined,
	EyeOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Flex, Menu, Popconfirm, message } from 'antd';

import {
	useDeleteCreatorFromCollection,
	useDeleteSingleCollection,
} from '@/api/monitor_collections';
import { CollectionContext } from '@/providers/CollectionsContextProvider';

type TProps = {
	type: 'videos' | 'creators';
	record: any;
	onViewClick?: () => void;
	onEditClick: () => void;
	isSingleCreatorsCollection?: boolean;
};

interface IResponseError {
	response: {
		data: {
			message: string;
			error: string;
		};
	};
	message: string;
}

export default function CollectionsMultiButton({
	record,
	type,
	onViewClick,
	onEditClick,
	isSingleCreatorsCollection = false,
}: TProps) {
	const { data: collectionStoreData } = useContext(CollectionContext);

	const { mutate: deleteCollection, isLoading: isDeleteLoading } =
		useDeleteSingleCollection(type);

	const { mutate: deleteCreator, isLoading: isDeleteCreatorLoading } =
		useDeleteCreatorFromCollection(collectionStoreData?.id);

	const onDeleteCollectionClick = () => {
		deleteCollection(
			{
				id: record?.id,
				type,
			},
			{
				onSuccess: () => {
					message.success('Removed');
				},
				onError: (error) => {
					const err = error as IResponseError;
					message.error(
						`An error occured: ${err?.response?.data?.message || err?.message}`,
					);
				},
			},
		);
	};

	const onDeleteCreatorClick = () => {
		deleteCreator(
			{
				id: record?.user_creators_collection_item_id,
				collection_id: collectionStoreData?.id,
			},
			{
				onSuccess: () => {
					message.success('Removed');
				},
				onError: (error) => {
					const err = error as IResponseError;
					message.error(
						`An error occured: ${err?.response?.data?.message || err?.message}`,
					);
				},
			},
		);
	};

	const handleMenuClick: MenuProps['onClick'] = (e) => {
		switch (e.key) {
			case 'view':
				onViewClick && onViewClick();
				break;
			case 'edit':
				onEditClick();
				break;
			case 'delete':
				e.domEvent.preventDefault();
				e.domEvent.stopPropagation();
				break;
			default:
				break;
		}
	};

	const menu = (
		<Menu onClick={handleMenuClick}>
			{!isSingleCreatorsCollection && (
				<Menu.Item key="view">
					<EyeOutlined style={{ fontSize: '14px', marginRight: '8px' }} />
					<span>View collection</span>
				</Menu.Item>
			)}

			<Menu.Item key="edit">
				<EditOutlined style={{ fontSize: '14px', marginRight: '8px' }} />
				<span>Edit</span>
			</Menu.Item>
			<Menu.Item
				key="delete"
				disabled={isDeleteLoading || isDeleteCreatorLoading}
				style={{ width: 'auto' }}
			>
				<Popconfirm
					title={
						isSingleCreatorsCollection
							? 'Sure to remove this creator?'
							: 'Sure to remove this collection?'
					}
					onConfirm={() =>
						isSingleCreatorsCollection
							? onDeleteCreatorClick()
							: onDeleteCollectionClick()
					}
					okText="Yes"
					cancelText="No"
					arrow={false}
					style={{ width: '100%' }}
				>
					<Flex style={{ width: '100%' }} gap={8} align="center">
						<DeleteOutlined style={{ fontSize: '14px' }} />
						<span>Remove</span>
					</Flex>
				</Popconfirm>
			</Menu.Item>
		</Menu>
	);

	return (
		<>
			<Dropdown overlay={menu} placement="bottomRight">
				<Button
					size="small"
					// disabled={isDeleteLoading || isDeleteCreatorLoading}
				>
					{isDeleteLoading || isDeleteCreatorLoading ? (
						<SyncOutlined spin />
					) : (
						<EllipsisOutlined />
					)}
				</Button>
			</Dropdown>
		</>
	);
}
